import './app/global.css';
import { initClient, initClientNavigation } from 'rwsdk/client';

function patchFetchToIncludeOriginOnPost(): void {
  if (typeof window === 'undefined') {
    return;
  }

  const originalFetch = window.fetch.bind(window);
  window.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
    if (init?.method !== 'POST') {
      return originalFetch(input, init);
    }

    const headers = new Headers(init.headers);
    if (!headers.has('Origin')) {
      headers.set('Origin', window.location.origin);
    }

    return originalFetch(input, { ...init, headers });
  };
}

patchFetchToIncludeOriginOnPost();

/**
 * URL schemes the browser (or the OS) has to handle itself.
 */
const BROWSER_HANDLED_LINK_SCHEMES = [
  'mailto:',
  'tel:',
  'sms:',
  'callto:',
  'geo:',
];

/**
 * rwsdk's client router intercepts every anchor click whose `href` does not
 * start with `http` and pushes the href through `history.pushState`. For a
 * `mailto:` link that throws a SecurityError and the link does nothing at all —
 * the visitor's mail client never opens.
 *
 * Registering on the capture phase *before* `initClientNavigation()` puts this
 * listener ahead of the router's, and `stopImmediatePropagation()` keeps the
 * router from ever seeing the click. `preventDefault()` is deliberately not
 * called, so the browser performs its normal action and the mail client opens.
 *
 * Contact calls-to-action are unaffected: after hydration they render as
 * `<button>`, which has no `href` for either listener to match.
 */
function letBrowserHandleNonHttpLinkClicks(): void {
  if (typeof document === 'undefined') {
    return;
  }

  document.addEventListener(
    'click',
    (event) => {
      const eventTarget = event.target;
      if (!(eventTarget instanceof Element)) {
        return;
      }

      const clickedAnchor = eventTarget.closest('a');
      const anchorHref = clickedAnchor?.getAttribute('href')?.toLowerCase();
      if (!anchorHref) {
        return;
      }

      if (
        BROWSER_HANDLED_LINK_SCHEMES.some((scheme) =>
          anchorHref.startsWith(scheme),
        )
      ) {
        event.stopImmediatePropagation();
      }
    },
    true,
  );
}

letBrowserHandleNonHttpLinkClicks();

const { handleResponse, onHydrated } = initClientNavigation();
initClient({ handleResponse, onHydrated });
